import { company } from '~/company/Company';
import { splitFormattedPrice } from '~/utils/formaters';

/** Codepoints of the Basic Multilingual Plane's Private Use Area. */
const PRIVATE_USE_AREA = /[\uE000-\uF8FF]/;

export interface PriceTextProps {
  /** An already formatted price, e.g. the output of `formatPriceWithCurrency`. */
  children: string;
}

/**
 * A formatted price whose currency is announced by assistive tech.
 *
 * Some tenants draw their currency as a glyph mapped to a Private Use Area
 * codepoint in a custom font — CityDrinks' dirham sits at U+F101, served by
 * `dirham-font` as a fallback in every `--fontFamily*` stack. That codepoint
 * means nothing outside the font, so a screen reader announces nothing in its
 * place and the price is read as a bare number. Where one is found the glyph is
 * dropped from the accessibility tree and the ISO currency code is exposed next
 * to the amount instead, leaving the visual rendering byte-identical.
 *
 * The ISO code also lands in `textContent`, which helps anything extracting the
 * price as text. That is a side effect, not a guarantee: a crawler is free to
 * discount clipped text, and for search engines the authoritative source of the
 * currency remains the Product/Offer JSON-LD built in `entry-server.tsx`.
 */
const PriceText = ({ children }: PriceTextProps) => {
  const { currencyCode } = company;

  if (!currencyCode || !PRIVATE_USE_AREA.test(children)) {
    return <>{children}</>;
  }

  const { prefix, amount, suffix } = splitFormattedPrice(children);

  // Only a symbol standing before the amount is handled. Anything else — no
  // amount to speak of, or a trailing symbol whose reading order would differ —
  // passes through untouched rather than being dropped or reordered.
  if (!amount || suffix) {
    return <>{children}</>;
  }

  return (
    <>
      <span aria-hidden="true">{prefix}</span>
      <span className="sr-only">{`${currencyCode} `}</span>
      {amount}
    </>
  );
};

export default PriceText;
